/**
 * El firmware de trazabilidad, en el único lugar donde está escrito.
 *
 * Va en dos formas, y las dos salen de FIRMWARE:
 *   - embebido en el código del usuario, al momento de mandarlo a compilar
 *     (embedFirmware). El usuario nunca lo ve: ni en el panel de código, ni en el
 *     proyecto guardado, ni en la cola de la sesión remota.
 *   - como sketch propio, el que se graba cuando la placa no lo tiene
 *     (FIRMWARE_SKETCH). Es el mismo embebido sobre un sketch vacío, así que los
 *     dos no se pueden desincronizar.
 *
 * Sale de Libreria/ProtocoloEDU/example/Basico/Basico.ino del repo de
 * trazabilidad: si el ejemplo cambia allá, lo que cambia es FIRMWARE. Ver
 * ProtocoloEDU/library.properties para la versión de la librería que tiene que
 * estar instalada en el builder: esto es la 3.1.0, y con una librería 2.x no
 * compila. Es el mismo para todas las placas: desde la 3.0 la librería no recibe
 * perfil y resuelve todo por plataforma al compilar.
 *
 * Lo del usuario que bloquea el loop -- un delay(), un while -- deja a
 * sistema.run() sin atender mientras dura, y la placa no contesta los pings en
 * ese rato. Ver PROBE_WINDOW_MS en traceabilityLink.
 */
export const FIRMWARE = {
  // Van arriba de todo el código: el include es lo que hace que el builder sume
  // la librería a la compilación.
  header: [
    '#include <ProtocoloEDU.h>',
    'ProtocoloEDU sistema;',
  ],
  // Primera instrucción de setup() y de loop().
  setup: 'sistema.begin();',
  loop: 'sistema.run();',
};

/**
 * El código con los comentarios y los literales de texto tapados con espacios,
 * del mismo largo y con los mismos saltos de línea. Es sobre lo que se buscan
 * setup() y loop(): un setup viejo comentado, o un "void loop() {" adentro de un
 * Serial.print, no tienen que contar. Las posiciones valen igual en el original.
 *
 * Los textos se pueden dejar: el nombre de un #include "..." también va entre
 * comillas.
 */
const maskCode = (code, { strings = true } = {}) => {
  const out = code.split('');
  const blank = (from, to) => {
    for (let i = from; i < to; i += 1) if (out[i] !== '\n') out[i] = ' ';
  };
  let i = 0;
  while (i < code.length) {
    const char = code[i];
    const next = code[i + 1];
    if (char === '/' && next === '/') {
      const end = code.indexOf('\n', i);
      const stop = end === -1 ? code.length : end;
      blank(i, stop);
      i = stop;
    } else if (char === '/' && next === '*') {
      const end = code.indexOf('*/', i + 2);
      const stop = end === -1 ? code.length : end + 2;
      blank(i, stop);
      i = stop;
    } else if (strings && (char === '"' || char === '\'')) {
      let j = i + 1;
      while (j < code.length && code[j] !== char && code[j] !== '\n') j += code[j] === '\\' ? 2 : 1;
      const stop = Math.min(j + 1, code.length);
      blank(i, stop);
      i = stop;
    } else {
      i += 1;
    }
  }
  return out.join('');
};

const INCLUDE = /#\s*include\s*[<"]ProtocoloEDU\.h[>"]/;

// Dónde abre el cuerpo de una función sin argumentos: justo después de su llave.
const bodyStart = (masked, name) => {
  const match = new RegExp(`\\bvoid\\s+${name}\\s*\\(\\s*(?:void\\s*)?\\)\\s*\\{`).exec(masked);
  return match ? match.index + match[0].length : -1;
};

// Si el código ya trae la librería, sea porque ya se embebió o porque la escribió
// a mano alguien que sabe lo que hace.
export const hasFirmware = (code = '') => INCLUDE.test(maskCode(code, { strings: false }));

/**
 * El código con el firmware adentro: el encabezado arriba de todo, y las llamadas
 * pegadas a la llave de setup() y de loop(), en la misma línea. Así lo único que
 * corre los números de línea de los errores de compilación es el encabezado.
 *
 * Se devuelve tal cual el que ya trae la librería -- embeberlo de nuevo son dos
 * `sistema` -- y el que no tiene setup() o loop(), que no compila de ninguna
 * manera y es mejor que el error que vea el usuario sea el suyo.
 */
export const embedFirmware = (code = '') => {
  if (hasFirmware(code)) return code;
  const masked = maskCode(code);
  const setupAt = bodyStart(masked, 'setup');
  const loopAt = bodyStart(masked, 'loop');
  if (setupAt === -1 || loopAt === -1) return code;

  // De atrás para adelante, para que la primera inserción no corra a la segunda.
  const insertions = [[setupAt, FIRMWARE.setup], [loopAt, FIRMWARE.loop]].sort((a, b) => b[0] - a[0]);
  const body = insertions.reduce((text, [at, call]) => `${text.slice(0, at)}${call}${text.slice(at)}`, code);
  return `${FIRMWARE.header.join('\n')}\n${body}`;
};

/**
 * El baudrate del primer Serial.begin() con un número literal, o null. Es la
 * velocidad a la que va a quedar hablando la placa: el Serial.begin() del usuario
 * corre después de sistema.begin() y le cambia la UART al firmware también.
 */
export const declaredBaudRate = (code = '') => {
  const match = /\bSerial\s*\.\s*begin\s*\(\s*(\d+)\s*[,)]/.exec(maskCode(code));
  return match ? Number(match[1]) : null;
};

const BLANK_SKETCH = `/**
 * Firmware de trazabilidad Educabot.
 *
 * Este código lo instala Educablocks para poder reconocer la placa y los módulos
 * conectados. No hace falta editarlo.
 */
void setup() {
}

void loop() {
}
`;

/**
 * El que se graba cuando la placa no tiene el firmware. No hace nada más que
 * atender al protocolo.
 */
export const FIRMWARE_SKETCH = embedFirmware(BLANK_SKETCH);

export const buildFirmwareSketch = () => FIRMWARE_SKETCH;

export default buildFirmwareSketch;
